/** Analyse locale du média chargé : aucun envoi, nom de marque ou récupération réseau.
 * Un fond opaque uniforme est écarté pour ne pas confondre les marges blanches d'un JPEG
 * avec un logo blanc. Les pixels transparents ne contribuent pas au calcul.
 */
export function logoNeedsDarkBackground(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): boolean {
  if (width < 1 || height < 1 || data.length !== width * height * 4) return false;
  const corners = [0, width - 1, (height - 1) * width, width * height - 1];
  const first = corners[0]! * 4;
  const background = [data[first]!, data[first + 1]!, data[first + 2]!];
  const uniformOpaqueBackground = corners.every((pixel) => {
    const offset = pixel * 4;
    return (
      data[offset + 3]! >= 240 &&
      background.every((value, channel) => Math.abs(data[offset + channel]! - value) <= 16)
    );
  });
  let foreground = 0,
    light = 0;
  for (let offset = 0; offset < data.length; offset += 4) {
    const alpha = data[offset + 3]! / 255;
    if (alpha < 0.15) continue;
    if (
      uniformOpaqueBackground &&
      background.every((value, channel) => Math.abs(data[offset + channel]! - value) <= 24)
    )
      continue;
    const luminance =
      (0.2126 * data[offset]! + 0.7152 * data[offset + 1]! + 0.0722 * data[offset + 2]!) / 255;
    foreground += alpha;
    if (luminance >= 0.72) light += alpha;
  }
  return foreground > 0 && light / foreground >= 0.65;
}

export function imageNeedsDarkBackground(image: HTMLImageElement): boolean {
  try {
    const scale = Math.min(1, 96 / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return false;
    context.drawImage(image, 0, 0, width, height);
    return logoNeedsDarkBackground(context.getImageData(0, 0, width, height).data, width, height);
  } catch {
    // Le média peut être affiché sans lecture de pixels (par exemple un CDN sans CORS).
    // Ne jamais réessayer via un proxy ou déduire sa couleur du nom de l'organisation.
    return false;
  }
}
